import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Crop, Sun, Contrast, RotateCcw, Check, X } from 'lucide-react';

// Función para crear imagen con ajustes
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

// Función para obtener el área recortada
async function getCroppedImg(imageSrc, pixelCrop, brightness = 100, contrast = 100) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) return null;

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Aplicar filtros de brillo y contraste
  ctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
}

function ImageEditor({ imageSrc, onSave, onCancel }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [activeTab, setActiveTab] = useState('crop'); // crop, adjust

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    try {
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        brightness,
        contrast
      );
      onSave(croppedImage);
    } catch (e) {
      console.error('Error al procesar imagen:', e);
    }
  };

  const resetAdjustments = () => {
    setBrightness(100);
    setContrast(100);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-neutral-900 border-b border-neutral-800">
        <Button
          variant="ghost"
          onClick={onCancel}
          className="text-neutral-400 hover:text-white"
        >
          <X className="h-5 w-5 mr-2" />
          Cancelar
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'crop' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('crop')}
            className={activeTab === 'crop' ? 'bg-white text-black' : 'text-neutral-400'}
            size="sm"
          >
            <Crop className="h-4 w-4 mr-2" />
            Recortar
          </Button>
          <Button
            variant={activeTab === 'adjust' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('adjust')}
            className={activeTab === 'adjust' ? 'bg-white text-black' : 'text-neutral-400'}
            size="sm"
          >
            <Sun className="h-4 w-4 mr-2" />
            Ajustes
          </Button>
        </div>

        <Button
          onClick={handleSave}
          className="bg-green-600 hover:bg-green-500 text-white"
        >
          <Check className="h-5 w-5 mr-2" />
          Aplicar
        </Button>
      </div>

      {/* Área del editor */}
      <div className="flex-1 relative">
        <div 
          className="absolute inset-0"
          style={{
            filter: `brightness(${brightness}%) contrast(${contrast}%)`
          }}
        >
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={undefined}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={activeTab === 'crop'}
          />
        </div>
      </div>

      {/* Controles inferiores */}
      <div className="bg-neutral-900 border-t border-neutral-800 p-4">
        {activeTab === 'crop' ? (
          <div className="max-w-md mx-auto space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-neutral-400 text-sm">Zoom</Label>
                <span className="text-neutral-500 text-xs">{zoom.toFixed(1)}x</span>
              </div>
              <Slider
                value={[zoom]}
                min={1}
                max={3}
                step={0.1}
                onValueChange={(value) => setZoom(value[0])}
                className="w-full"
              />
            </div>
            <p className="text-neutral-500 text-xs text-center">
              Arrastra la imagen para reposicionar. Usa el zoom para acercar.
            </p>
          </div>
        ) : (
          <div className="max-w-md mx-auto space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-neutral-400 text-sm flex items-center gap-2">
                  <Sun className="h-4 w-4" /> Brillo
                </Label>
                <span className="text-neutral-500 text-xs">{brightness}%</span>
              </div>
              <Slider
                value={[brightness]}
                min={50}
                max={150}
                step={1}
                onValueChange={(value) => setBrightness(value[0])}
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-neutral-400 text-sm flex items-center gap-2">
                  <Contrast className="h-4 w-4" /> Contraste
                </Label>
                <span className="text-neutral-500 text-xs">{contrast}%</span>
              </div>
              <Slider
                value={[contrast]}
                min={50}
                max={150}
                step={1}
                onValueChange={(value) => setContrast(value[0])}
                className="w-full"
              />
            </div>

            <Button
              variant="outline"
              onClick={resetAdjustments}
              className="w-full border-neutral-700 text-neutral-400 hover:bg-neutral-800"
              size="sm"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Restablecer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageEditor;
